import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  IconButton,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText
} from '@mui/material';
import {
  Menu as MenuIcon,
} from '@mui/icons-material';
import AppLogo from './common/AppLogo';

const Navigation: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const config = window.__APP_CONFIG__;
  const adminUrl = config?.ADMIN_URL || 'http://localhost:3000';

  const navItems = [
    { label: 'Documentation', href: 'https://docs.familycast.app', external: true },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Products', href: '/products' },
    { label: 'Beta', href: '/beta' },
    { label: 'About', href: '/about', dropdown: true },
    { label: 'Contact', href: '/contact' },
    { label: 'My Family', href: `${adminUrl}`, external: true }
  ];

  const aboutMenuItems = [
    { label: 'About Us', href: '/about', external: false },
    { label: 'Privacy Policy', href: '/privacy', external: false },
    { label: 'Terms of Service', href: '/tos', external: false },
  ];
  const [aboutAnchorEl, setAboutAnchorEl] = useState<null | HTMLElement>(null);
  const handleAboutMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAboutAnchorEl(event.currentTarget);
  };
  const handleAboutClose = () => {
    setAboutAnchorEl(null);
  };

  return (
    <AppBar
      position="static"
      color="transparent"
      elevation={0}
      sx={{
        bgcolor: 'transparent !important',
        background: 'transparent !important',
        boxShadow: 'none',
        border: 'none'
      }}
    >
      <Container maxWidth="lg">
        <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <AppLogo
                variant="with-text"
                color="white"
                height={32}
                sx={{ opacity: 0.9 }}
              />
            </Link>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {navItems.map((item) =>
                item.label === 'About' && item.dropdown ? (
                  <React.Fragment key={item.label}>
                    <Button
                      onClick={handleAboutMenu}
                      sx={{
                        color: 'white',
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 500,
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.1)',
                          color: 'white',
                        },
                      }}
                    >
                      About
                    </Button>
                    <Menu
                      anchorEl={aboutAnchorEl}
                      open={Boolean(aboutAnchorEl)}
                      onClose={handleAboutClose}
                    >
                      {aboutMenuItems.map((aboutItem) => (
                        <MenuItem
                          key={aboutItem.label}
                          {...(aboutItem.external
                            ? { component: 'a', href: aboutItem.href, target: '_blank', rel: 'noopener noreferrer' }
                            : { component: Link, to: aboutItem.href }
                          )}
                          onClick={handleAboutClose}
                        >
                          {aboutItem.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </React.Fragment>
                ) : item.external ? (
                  <Button
                    key={item.label}
                    component="a"
                    href={item.href}
                    {...(item.label === 'My Family' ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                    sx={{
                      color: 'white',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.1)',
                        color: 'white',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ) : (
                  <Button
                    key={item.label}
                    component={Link}
                    to={item.href}
                    sx={{
                      color: 'white',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.1)',
                        color: 'white',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                )
              )}
              
              <Button
                variant="outlined"
                component="a"
                href={`${adminUrl}/login`}
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    borderColor: 'white'
                  }
                }}
              >
                Sign In
              </Button>
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                component="a"
                href={`${adminUrl}/login`}
                size="small"
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    borderColor: 'white'
                  }
                }}
              >
                Sign In
              </Button>
              
              <IconButton
                onClick={() => setMobileMenuOpen(true)}
                sx={{
                  color: 'white'
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </Container>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      >
        <Box
          sx={{ width: 280, pt: 2 }}
          role="presentation"
        >
          <List>
            <ListItem disablePadding>
              <ListItemButton
                component="a"
                href={`${adminUrl}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <ListItemText primary="My Family" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/pricing"
                onClick={() => setMobileMenuOpen(false)}
              >
                <ListItemText primary="Pricing" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/products"
                onClick={() => setMobileMenuOpen(false)}
              >
                <ListItemText primary="Products" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/beta"
                onClick={() => setMobileMenuOpen(false)}
              >
                <ListItemText primary="Beta" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
              >
                <ListItemText primary="Contact" />
              </ListItemButton>
            </ListItem>

            <Divider sx={{ my: 1 }} />

            {/* About submenu */}
            <ListItem>
              <Typography variant="caption" sx={{ color: "text.secondary" }} fontWeight="bold">
                ABOUT
              </Typography>
            </ListItem>
            {aboutMenuItems.map((item) => (
              <ListItem key={item.label} disablePadding>
                <ListItemButton
                  {...(item.external
                    ? {
                        component: 'a',
                        href: item.href,
                        target: '_blank',
                        rel: 'noopener noreferrer'
                      }
                    : {
                        component: Link,
                        to: item.href
                      }
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                  sx={{ pl: 4 }}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}

            <Divider sx={{ my: 1 }} />

            {/* External links */}
            <ListItem disablePadding>
              <ListItemButton
                component="a"
                href="https://docs.familycast.app"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  setMobileMenuOpen(false);
                }}
              >
                <ListItemText primary="Documentation" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Navigation;
